import React, { useState, useEffect, useRef } from 'react';
import { sendMessage, getChatHistory, generateImage, sendGuestMessage, regenerateAvatar } from './api';
import './ChatPage.css';

const MAX_GUEST_MESSAGES = 5;

function ChatPage({ character, user, onBack, onCreditsUpdate, onShowAuth, onNeedCredits, guestMode = false, onGuestLimitReached }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(!guestMode);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageNsfw, setImageNsfw] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [guestMessageCount, setGuestMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [charData, setCharData] = useState(character);

  useEffect(() => {
    setCharData(character);
  }, [character]);

  // Auto-regenerate avatar if missing
  useEffect(() => {
    if (!guestMode && charData.id && !charData.avatar_url) {
      regenerateAvatar(charData.id)
        .then(res => setCharData(res.data))
        .catch(() => {}); // silent fail
    }
  }, [charData.id, charData.avatar_url, guestMode]);

  useEffect(() => {
    if (!guestMode && character.id) {
      loadHistory();
    } else {
      setLoadingHistory(false);
    }
  }, [character.id, guestMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await getChatHistory(character.id);
      const msgs = res.data.map(m => ({
        id: m.id,
        role: m.sender === 'user' ? 'user' : 'ai',
        content: m.content,
        image_url: m.image_url,
        is_image: m.is_image,
        timestamp: m.timestamp,
      }));
      setMessages(msgs);
    } catch {
      // empty conversation
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    // Guest mode: check message limit
    if (guestMode) {
      if (guestMessageCount >= MAX_GUEST_MESSAGES) {
        onGuestLimitReached?.();
        return;
      }
    } else {
      // Logged-in mode: premium users have unlimited chat, only check credits for free-tier
      if (user && !user.is_premium && user.credits < 1) {
        onNeedCredits?.();
        return;
      }
    }

    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text, id: Date.now() }]);
    setLoading(true);

    try {
      if (guestMode) {
        // Build history for guest endpoint
        const historyForApi = [...messages, { role: 'user', content: text }].map(m => ({
          role: m.role,
          content: m.content || '',
        }));
        const res = await sendGuestMessage(text, character, historyForApi);
        setMessages(prev => [...prev, {
          role: 'ai',
          content: res.data.response,
          id: Date.now() + 1,
        }]);
        const newCount = guestMessageCount + 1;
        setGuestMessageCount(newCount);

        // After 5th message, trigger limit
        if (newCount >= MAX_GUEST_MESSAGES) {
          setTimeout(() => onGuestLimitReached?.(), 1500);
        }
      } else {
        const res = await sendMessage(character.id, text);
        setMessages(prev => [...prev, {
          role: 'ai',
          content: res.data.response,
          id: Date.now() + 1,
        }]);
        onCreditsUpdate?.(res.data.credits);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 402 || (guestMode && (status === 403 || status === 429))) {
        if (guestMode) {
          onGuestLimitReached?.();
        } else {
          onNeedCredits?.();
        }
        return;
      }
      const detail = err.response?.data?.detail || 'Something went wrong.';
      setMessages(prev => [...prev, { role: 'ai', content: detail, id: Date.now() + 1, isError: true }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handlePhotoRequest = () => {
    if (guestMode) {
      onGuestLimitReached?.();
      return;
    }
    setShowImageModal(true);
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim() || generatingImage) return;

    setGeneratingImage(true);

    try {
      const res = await generateImage(character.id, imagePrompt.trim(), imageNsfw);
      setMessages(prev => [...prev, {
        role: 'ai',
        content: '',
        is_image: true,
        image_url: res.data.image_url,
        id: Date.now(),
      }]);
      onCreditsUpdate?.(res.data.credits);
      setShowImageModal(false);
      setImagePrompt('');
      setImageNsfw(false);
    } catch (err) {
      if (err.response?.status === 402) {
        setShowImageModal(false);
        onNeedCredits?.();
        return;
      }
      alert(err.response?.data?.detail || 'Image generation failed.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const remainingGuest = guestMode ? MAX_GUEST_MESSAGES - guestMessageCount : null;

  return (
    <div className="chat-page">
      {/* Sidebar */}
      <aside className="chat-sidebar">
        <button className="back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>

        <div className="char-profile">
          <div className="char-profile-avatar">
            <AvatarWithFallback
              url={charData.avatar_url}
              name={character.name}
              size="lg"
            />
            <div className="online-dot" />
          </div>
          <h2 className="char-profile-name">{character.name}</h2>
          <p className="char-profile-age">{character.age} years old</p>
          {character.description && (
            <p className="char-profile-desc">{character.description.slice(0, 80)}{character.description.length > 80 ? '...' : ''}</p>
          )}
        </div>

        <div className="sidebar-divider" />

        <div className="sidebar-actions">
          <button
            className="sidebar-action-btn"
            onClick={handlePhotoRequest}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
            Request photo
          </button>
        </div>

        <div className="sidebar-credits">
          {guestMode ? (
            <>
              <span className="credits-label">Free messages left</span>
              <span className="credits-value">{remainingGuest}</span>
              <span className="credits-costs">
                Create an account for more
              </span>
            </>
          ) : (
            <>
              <span className="credits-label">Your credits</span>
              <span className="credits-value">{user?.credits ?? 0}</span>
              <span className="credits-costs">
                {user?.is_premium ? 'Chat: Free' : 'Chat: 1 cr'} &nbsp;|&nbsp; Photo: 7 cr &nbsp;|&nbsp; NSFW: 15 cr
              </span>
            </>
          )}
        </div>
      </aside>

      {/* Main chat */}
      <div className="chat-main">
        {/* Chat header (mobile) */}
        <div className="chat-topbar">
          <button className="back-btn-mobile" onClick={onBack}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div className="topbar-char">
            <div className="topbar-avatar">
              <AvatarWithFallback url={charData.avatar_url} name={character.name} size="sm" />
            </div>
            <div>
              <div className="topbar-name">{character.name}</div>
              <div className="topbar-status">online</div>
            </div>
          </div>
          <button className="topbar-photo-btn" onClick={handlePhotoRequest}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <path d="M21 15l-5-5L5 21"/>
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="messages-container">
          {loadingHistory ? (
            <div className="chat-loading">
              <div className="loading-dots"><span /><span /><span /></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">
              <div className="chat-empty-avatar">
                <AvatarWithFallback url={charData.avatar_url} name={character.name} size="lg" />
              </div>
              <p className="chat-empty-name">Start a conversation with {character.name}</p>
              <p className="chat-empty-hint">Say hi, ask anything, or request a photo</p>
            </div>
          ) : (
            messages.map((msg) => (
              <MessageBubble key={msg.id} msg={msg} charName={character.name} charAvatar={charData.avatar_url} formatTime={formatTime} />
            ))
          )}

          {loading && (
            <div className="message-row ai">
              <div className="msg-avatar sm">
                <AvatarWithFallback url={charData.avatar_url} name={character.name} size="sm" />
              </div>
              <div className="bubble bubble-ai typing-bubble">
                <span /><span /><span />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Guest limit banner */}
        {guestMode && guestMessageCount >= MAX_GUEST_MESSAGES && (
          <div style={{
            padding: '14px 20px',
            background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(236,72,153,0.15))',
            borderTop: '1px solid rgba(168,85,247,0.3)',
            textAlign: 'center',
            fontSize: '14px',
            color: 'var(--text)',
          }}>
            You've used all 5 free messages.{' '}
            <button
              onClick={() => onGuestLimitReached?.()}
              style={{
                background: 'none', border: 'none', color: 'var(--primary)',
                fontWeight: '600', cursor: 'pointer', fontSize: '14px', textDecoration: 'underline',
              }}
            >
              Create an account to continue
            </button>
          </div>
        )}

        {/* Input */}
        <div className="chat-input-wrap">
          <div className="chat-input-box">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={guestMode && guestMessageCount >= MAX_GUEST_MESSAGES
                ? 'Create an account to continue chatting...'
                : `Message ${character.name}...`}
              disabled={loading || (guestMode && guestMessageCount >= MAX_GUEST_MESSAGES)}
            />
            <button
              className="photo-quick-btn"
              onClick={handlePhotoRequest}
              title="Request photo"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
            </button>
            <button
              className="send-btn"
              onClick={handleSend}
              disabled={loading || !input.trim() || (guestMode && guestMessageCount >= MAX_GUEST_MESSAGES)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Image generation modal */}
      {showImageModal && (
        <div className="overlay" onClick={(e) => e.target === e.currentTarget && setShowImageModal(false)}>
          <div className="modal" style={{ maxWidth: '460px' }}>
            <button className="modal-close" onClick={() => setShowImageModal(false)}>&#x2715;</button>
            <h2 className="modal-title">Request a photo</h2>

            <div className="form-field">
              <label>What should she be doing?</label>
              <input
                type="text"
                placeholder="e.g. relaxing on the beach, in a dress..."
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerateImage()}
                autoFocus
              />
            </div>

            <div className="nsfw-toggle">
              <div className="nsfw-info">
                <div className="nsfw-label">{imageNsfw ? 'Explicit (NSFW)' : 'Standard'}</div>
                <div className="nsfw-cost">
                  {imageNsfw ? '15 credits' : '7 credits'}
                </div>
              </div>
              <button
                className={`toggle-btn ${imageNsfw ? 'active' : ''}`}
                onClick={() => setImageNsfw(!imageNsfw)}
              >
                <span className="toggle-knob" />
              </button>
            </div>

            <div className="img-cost-note">
              You have <strong style={{ color: 'var(--primary)' }}>{user?.credits ?? 0}</strong> credits.
              This will cost <strong style={{ color: imageNsfw ? 'var(--pink)' : 'var(--primary)' }}>{imageNsfw ? 15 : 7}</strong> credits.
            </div>

            <button
              className="btn-primary"
              onClick={handleGenerateImage}
              disabled={generatingImage || !imagePrompt.trim()}
              style={{ width: '100%', padding: '14px', fontSize: '15px', borderRadius: '12px', marginTop: '16px' }}
            >
              {generatingImage ? 'Generating...' : 'Generate photo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function AvatarWithFallback({ url, name, size = '' }) {
  const [imgError, setImgError] = React.useState(false);
  const initial = name ? name.charAt(0).toUpperCase() : '?';

  if (url && !imgError) {
    return (
      <img
        src={url}
        alt={name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className={`avatar-placeholder ${size}`}>{initial}</div>
  );
}

function MessageBubble({ msg, charName, charAvatar, formatTime }) {
  const isUser = msg.role === 'user';

  return (
    <div className={`message-row ${isUser ? 'user' : 'ai'}`}>
      {!isUser && (
        <div className="msg-avatar">
          {charAvatar
            ? <img src={charAvatar} alt="" />
            : <div className="avatar-placeholder sm">{charName.charAt(0)}</div>
          }
        </div>
      )}

      <div className="bubble-wrap">
        {msg.is_image && msg.image_url ? (
          <div className={`bubble bubble-image ${isUser ? 'bubble-user' : 'bubble-ai'}`}>
            <img
              src={msg.image_url}
              alt="Generated"
              className="chat-image"
              onClick={() => window.open(msg.image_url, '_blank')}
            />
          </div>
        ) : (
          <div className={`bubble ${isUser ? 'bubble-user' : 'bubble-ai'} ${msg.isError ? 'bubble-error' : ''}`}>
            {msg.content}
          </div>
        )}
        {msg.timestamp && (
          <div className={`msg-time ${isUser ? 'right' : 'left'}`}>{formatTime(msg.timestamp)}</div>
        )}
      </div>
    </div>
  );
}

export default ChatPage;
